import { Card, Col, Collapse, Form, Row, Select } from "antd";
import moment from "moment";
import { forwardRef, useCallback, useEffect, useState } from "react";
import { isArrayAndHasValue } from "utils/functions";
import {
  GET_EXPORT_DETAILS_INVOICE,
  GET_SUBMISSION_REALIZATION_LIST,
  SUBMISSION_REALIZATION_ADD,
  SUBMISSION_REALIZATION_SALES_CONTRACT_LIST,
} from "../../../../apiServices/API_ENDPOINTS";
import { getData, postData } from "../../../../apiServices/common";
import { alertPop } from "../../../../apiServices/common/helper";
import InvoiceDetails from "./InvoiceDetails";
import InvoiceTableFilterFields from "./InvoiceTableFilterFields";

const SubmissionRealizationForm = forwardRef((props, ref) => {
  // Props
  const { form, editMode, exportId, invoiceDetails, view, fileList } = props;

  // States
  const [salesContractList, setSalesContractList] = useState([]);
  const [invoiceList, setInvoiceList] = useState([]);
  const [paymentTerm, setPaymentTerm] = useState(null);
  const [invoiceTableData, setInvoiceTableData] = useState([]);
  const [filteredInvoiceTableData, setFilteredInvoiceTableData] = useState([]);
  const [filesList, setFilesList] = useState([]);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);
  const [currentFilterValues, setCurrentFilterValues] = useState({});

  var document = [];
  var document_name = [];

  // Antd
  const sales_contract_id_watch = Form.useWatch("sales_contract_id", form);
  const [searchForm] = Form.useForm();

  useEffect(() => {
    // Assign data to invoiceTableData only when invoiceList is updated
    if (!editMode) {
      setInvoiceTableData(invoiceList || []);
      setFilteredInvoiceTableData(invoiceList || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceList]);

  useEffect(() => {
    if (isArrayAndHasValue(invoiceDetails)) {
      setInvoiceTableData(invoiceDetails);
      setFilteredInvoiceTableData(invoiceDetails);
    }
  }, [invoiceDetails]);

  useEffect(() => {
    // If editMode, fileList props is available and filesList is empty then set fileList to filesList
    if (!isArrayAndHasValue(filesList) && editMode) {
      isArrayAndHasValue(fileList) && setFilesList(fileList);
    }
  }, [filesList, fileList, editMode]);

  // Handle data changes while preserving filter state
  useEffect(() => {
    if (hasActiveFilters && Object.keys(currentFilterValues).length > 0) {
      // Reapply current filters when main data changes
      // Use a small delay to ensure all state updates are complete
      const timeoutId = setTimeout(() => {
        applyClientSideFilter(currentFilterValues);
      }, 10);

      return () => clearTimeout(timeoutId);
    } else {
      // No filters active, show all data
      setFilteredInvoiceTableData(invoiceTableData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceTableData]);

  const getSalesContractList = useCallback(
    async (filterValues) => {
      const payload = {
        ...filterValues,
      };

      const url = exportId
        ? `${SUBMISSION_REALIZATION_SALES_CONTRACT_LIST}?submission_realization_id=${exportId}`
        : SUBMISSION_REALIZATION_SALES_CONTRACT_LIST;
      const response = await getData(url, false, payload);

      if (response && response?.status === 200) {
        setSalesContractList(response?.data?.data || []);
      } else {
        alertPop("error", response?.messages ? response?.messages : "Something Went Wrong!");
      }
    },
    [exportId],
  );

  const getInvoiceList = useCallback(async (salesContractID, filterValues) => {
    let response = await getData(`${GET_EXPORT_DETAILS_INVOICE}/${salesContractID}`, false, filterValues);

    if (response && response?.status === 200) {
      setPaymentTerm(response?.data?.data?.payment_term);

      setInvoiceList(
        isArrayAndHasValue(response?.data?.data?.export_details) ? response?.data?.data?.export_details : [],
      );
    } else {
      alertPop("error", response?.messages ? response?.messages : "Something Went Wrong!");
    }
  }, []);

  const onSelectSalesContract = useCallback(
    (value) => {
      // Get invoice list by selected sales contract
      getInvoiceList(value);
    },
    [getInvoiceList],
  );

  // Effects
  useEffect(() => {
    getSalesContractList();
  }, [getSalesContractList]);

  useEffect(() => {
    if (editMode) {
      sales_contract_id_watch && getInvoiceList(sales_contract_id_watch);
      sales_contract_id_watch && onSelectSalesContract(sales_contract_id_watch);
    }
  }, [editMode, sales_contract_id_watch, onSelectSalesContract, getInvoiceList]);

  const onFinishSubmissionRealization = async (values) => {
    if (filesList?.length) {
      filesList.forEach((item) => {
        document_name.push(item?.name);
        document.push(item?.id);
      });
    }

    // Log to verify all data (filtered + unfiltered) is being sent
    console.log("=== Export Submission Update Debug ===");
    console.log("Total records in full dataset:", invoiceTableData?.length);
    console.log("Filtered records displayed:", filteredInvoiceTableData?.length);
    console.log("Has active filters:", hasActiveFilters);
    console.log("Sending ALL records to API (updated + unchanged)");

    const bodyData = {
      sales_contract_id: values?.sales_contract_id,
      invoice_items:
        isArrayAndHasValue(invoiceTableData) &&
        invoiceTableData?.map((item) => {
          return {
            id: item?.id || null,
            fdbc_number: item?.fdbc_number || null,
            fdbc_date: item?.fdbc_date ? moment(item?.fdbc_date).format("YYYY-MM-DD") : null,
            acceptance_rcv_date: item?.acceptance_rcv_date
              ? moment(item?.acceptance_rcv_date).format("YYYY-MM-DD")
              : null,
            maturity_date: item?.maturity_date ? moment(item?.maturity_date).format("YYYY-MM-DD") : null,
            realization_date: item?.realization_date ? moment(item?.realization_date).format("YYYY-MM-DD") : null,
            export_details_id: item?.export_details_id || null,
            invoice_no: item?.invoice_no || null,
            invoice_value: item?.invoice_value ? Number(item?.invoice_value) : null,
            invoice_date: item?.invoice_date ? moment(item?.invoice_date).format("YYYY-MM-DD") : null,
            realization_value: item?.realization_value ? Number(item?.realization_value) : null,
            conversion_rate: item?.conversion_rate ? Number(item?.conversion_rate) : null,
            converted_value: item?.converted_value ? Number(item?.converted_value) : null,
            short_value: item?.short_value ? Number(item?.short_value) : null,
            courier_no: item?.courier_no || null,
            courier_date: item?.courier_date ? moment(item?.courier_date).format("YYYY-MM-DD") : null,
            status: item?.status || null,
            courier_option_id: item?.courier_option_id || null,
            document_submission_date: item?.document_submission_date
              ? moment(item?.document_submission_date).format("YYYY-MM-DD")
              : null,
            file_id: item?.file_id || null,
            ...(item?.is_copied_file
              ? {
                  is_copied_file: 1,
                  copied_from_export_details_id: item?.copied_from_export_details_id || null,
                  copied_from_submission_invoice_id: item?.copied_from_submission_invoice_id || null,
                }
              : {
                  is_copied_file: 0,
                }),
          };
        }),
      document,
      document_name,
    };

    console.log("Submission Realization Body Data: ", bodyData);

    // return;

    if (editMode) {
      const response = await postData(`${GET_SUBMISSION_REALIZATION_LIST}/${exportId}/edit`, bodyData);

      if (response) {
        alertPop("success", "Submission Realization Updated Successfully");
      } else {
        alertPop("error", "Submission Realization Update Failed!");
      }
    } else {
      const response = await postData(SUBMISSION_REALIZATION_ADD, bodyData);

      if (response) {
        alertPop("success", "Submission Realization Added Successfully");
      } else {
        alertPop("error", "Submission Realization Add Failed!");
      }
    }
  };

  const onTableDataChange = (value, filteredIndex, context) => {
    // Find the corresponding item in filtered data
    const filteredItem = filteredInvoiceTableData[filteredIndex];

    if (!filteredItem) return;

    // Find the same item in the full data using a unique identifier
    const fullDataIndex = invoiceTableData.findIndex(
      (item) =>
        item?.export_details_id === filteredItem?.export_details_id ||
        (item?.invoice_no === filteredItem?.invoice_no && item?.invoice_date === filteredItem?.invoice_date),
    );

    if (fullDataIndex === -1) return;

    // Update the full data
    const tableDataCopy = [...invoiceTableData];
    tableDataCopy[fullDataIndex][context] = value;
    setInvoiceTableData(tableDataCopy);

    // Update the filtered data directly to avoid flickering
    const filteredTableDataCopy = [...filteredInvoiceTableData];
    filteredTableDataCopy[filteredIndex][context] = value;
    setFilteredInvoiceTableData(filteredTableDataCopy);
  };

  // Client-side filtering function
  const applyClientSideFilter = (filterValues) => {
    // Store current filter values
    setCurrentFilterValues(filterValues || {});

    if (
      !filterValues ||
      Object.keys(filterValues).every((key) => !filterValues[key] || filterValues[key].toString().trim() === "")
    ) {
      setFilteredInvoiceTableData(invoiceTableData);
      setHasActiveFilters(false);
      return;
    }

    setHasActiveFilters(true);

    const filtered = invoiceTableData.filter((item) => {
      let matches = true;

      // Filter by invoice_no
      if (filterValues.invoice_no && filterValues.invoice_no.trim() !== "") {
        matches = matches && item?.invoice_no?.toString().toLowerCase().includes(filterValues.invoice_no.toLowerCase());
      }

      // Filter by invoice_value
      if (filterValues.invoice_value && filterValues.invoice_value.trim() !== "") {
        matches = matches && item?.invoice_value?.toString().includes(filterValues.invoice_value);
      }

      // Filter by fdbc_number
      if (filterValues.fdbc_number && filterValues.fdbc_number.trim() !== "") {
        matches =
          matches && item?.fdbc_number?.toString().toLowerCase().includes(filterValues.fdbc_number.toLowerCase());
      }

      // Filter by realization_value
      if (filterValues.realization_value && filterValues.realization_value.trim() !== "") {
        matches = matches && item?.realization_value?.toString().includes(filterValues.realization_value);
      }

      // Filter by status
      if (filterValues.status && filterValues.status.trim() !== "") {
        matches = matches && item?.status === filterValues.status;
      }

      return matches;
    });

    setFilteredInvoiceTableData(filtered);
  };

  const onTableSearch = (values) => {
    const searchFormValues = searchForm.getFieldsValue();

    // Apply client-side filtering instead of server-side
    applyClientSideFilter(searchFormValues);
  };

  const onClearSearch = () => {
    searchForm.resetFields();

    // Reset filter state
    setHasActiveFilters(false);
    setCurrentFilterValues({});

    // Reset filtered data to show all invoice data
    setFilteredInvoiceTableData(invoiceTableData);
  };

  return (
    <div className="containt-body pt-3">
      <Form layout="vertical" form={form} name="control-hooks" onFinish={onFinishSubmissionRealization}>
        <Card>
          <Row gutter={6}>
            <Col span={4}>
              <Form.Item
                label="Sales Contract - Master LC"
                name="sales_contract_id"
                rules={[
                  {
                    required: true,
                    message: "Please Select Sales Contract",
                  },
                ]}
              >
                <Select
                  placeholder="Sales Contract - Master LC"
                  style={{ width: "100%" }}
                  onSelect={(value) => {
                    onSelectSalesContract(value);
                  }}
                  size="small"
                  // onSearch={(value) =>
                  //   getSalesContractList({ reference_no: value })
                  // }
                  filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                  onClear={() => {
                    getSalesContractList();
                    setInvoiceList([]);
                  }}
                  allowClear
                  showSearch
                  disabled={view}
                >
                  {salesContractList.map((option) => {
                    return (
                      <option key={option.id} value={option.id}>
                        {option.reference_no}
                      </option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Card>
      </Form>

      {/* Sewing Table Search Form */}
      <Collapse>
        <Collapse.Panel header={<b>Table Filter</b>} key="2">
          <Form form={searchForm} layout="vertical" onFinish={onTableSearch}>
            <InvoiceTableFilterFields onClearSearch={onClearSearch} />
          </Form>
        </Collapse.Panel>
      </Collapse>

      <InvoiceDetails
        onTableDataChange={onTableDataChange}
        invoiceTableData={filteredInvoiceTableData}
        fullInvoiceTableData={invoiceTableData}
        paymentTerm={paymentTerm}
        setInvoiceTableData={setInvoiceTableData}
        setFilteredInvoiceTableData={setFilteredInvoiceTableData}
        hasActiveFilters={hasActiveFilters}
        currentFilterValues={currentFilterValues}
        applyClientSideFilter={applyClientSideFilter}
        view={view}
      />
    </div>
  );
});

export default SubmissionRealizationForm;
